import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { SERVER_UNREACHABLE_EVENT } from '../constants/network';

const BURST_CHECK_COUNT = 6;
const BURST_INTERVAL_MS = 10_000;
const SAFE_INTERVAL_MS = 5 * 60_000;
const HEALTHCHECK_TIMEOUT_MS = 5_000;

export const NetworkContext = createContext({
  isOnline: true,
  isServerReachable: true,
  isChecking: false,
  checkServerHealth: () => {},
});

export const NetworkProvider = ({ children }) => {
  // Server render (marketing prerender) has no navigator: assume online, as the first client render does.
  const [isBrowserOnline, setIsBrowserOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [isChecking, setIsChecking] = useState(false);

  const isOnline = isBrowserOnline && isServerReachable;

  const checkServerHealth = useCallback(async () => {
    if (!navigator.onLine) {
      setIsServerReachable(false);
      return;
    }
    setIsChecking(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), HEALTHCHECK_TIMEOUT_MS);
      const isOk = await api.checkHealth(controller.signal);
      clearTimeout(timeoutId);
      setIsServerReachable(isOk);
    } catch {
      setIsServerReachable(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Browser online/offline + API-driven reachability events from utils/api.js
  useEffect(() => {
    const handleOnline = () => {
      setIsBrowserOnline(true);
      checkServerHealth();
    };
    const handleOffline = () => {
      setIsBrowserOnline(false);
      setIsServerReachable(false);
    };
    const handleServerUnreachable = () => setIsServerReachable(false);
    const handleServerReachable = () => setIsServerReachable(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener(SERVER_UNREACHABLE_EVENT, handleServerUnreachable);
    window.addEventListener('server-reachable', handleServerReachable);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(SERVER_UNREACHABLE_EVENT, handleServerUnreachable);
      window.removeEventListener('server-reachable', handleServerReachable);
    };
  }, [checkServerHealth]);

  // Initial check on mount
  useEffect(() => {
    checkServerHealth();
  }, [checkServerHealth]);

  // Adaptive polling:
  //   reachable   → safety check every 5 min
  //   unreachable → 6 bursts × 10 s during the first minute, then every 5 min
  useEffect(() => {
    if (isServerReachable) {
      const safeId = setInterval(checkServerHealth, SAFE_INTERVAL_MS);
      return () => clearInterval(safeId);
    }

    let burstCount = 0;
    const burstId = setInterval(() => {
      checkServerHealth();
      burstCount += 1;
      if (burstCount >= BURST_CHECK_COUNT) clearInterval(burstId);
    }, BURST_INTERVAL_MS);

    let safeId = null;
    const handoffId = setTimeout(() => {
      safeId = setInterval(checkServerHealth, SAFE_INTERVAL_MS);
    }, BURST_CHECK_COUNT * BURST_INTERVAL_MS + 1000);

    return () => {
      clearInterval(burstId);
      clearTimeout(handoffId);
      if (safeId) clearInterval(safeId);
    };
  }, [isServerReachable, checkServerHealth]);

  return (
    <NetworkContext.Provider value={{ isOnline, isServerReachable, isBrowserOnline, isChecking, checkServerHealth }}>
      {children}
    </NetworkContext.Provider>
  );
};
